import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setLanguage } from '../redux/store/device/Actions';
import { updateUser } from '../redux/store/userData/user/actions';
import { getBrandLanguage } from '../helpers/brandHelpers';
import { BrandData, UserDataOutput } from '../@types';
import { getTexts } from '../redux/store/appData/texts';

interface UseInitializeLanguageProps {
  xBrandId: string;
  brandSettings?: BrandData;
  user?: UserDataOutput;
  savedLang?: string;
  deviceLang?: string;
  forcedLang?: string | null;
}

/**
 * Custom hook to initialize and update the language settings based on:
 * - User's saved locale
 * - Browser/Device language
 * - Brand's allowed languages and default language
 */
export const useInitializeLanguage = ({
  xBrandId,
  brandSettings,
  user,
  deviceLang,
  savedLang,
  forcedLang = null,
}: UseInitializeLanguageProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Get the brand’s default language if nothing else matches
    const brandLang = getBrandLanguage(xBrandId);
    const allowedLanguages = brandSettings?.allowedLanguages || [];

    let finalLang: string | undefined;

    // Query forced lang forces locale if lang passed is allowed
    if (forcedLang && allowedLanguages?.includes(forcedLang)) {
      if (
        forcedLang === 'de' &&
        allowedLanguages.includes('deformal') &&
        !allowedLanguages.includes('de')
      ) {
        dispatch(setLanguage('de'));
      } else {
        dispatch(setLanguage(forcedLang));
      }
      return;
    } else if (user?.locale) {
      // If the user has a locale set, that takes priority
      if (allowedLanguages?.includes(user.locale)) {
        finalLang = user.locale;
      } else {
        // If user's saved language isn't allowed, fallback to brand's default
        finalLang = brandLang;
      }
    } else if (!savedLang && brandSettings) {
      // If there's no existing language in Redux / Guest, but we have brand settings:
      if (deviceLang && allowedLanguages?.includes(deviceLang)) {
        // If the browser language is allowed, use it
        finalLang = deviceLang;
      } else if (
        deviceLang &&
        deviceLang === 'de' &&
        allowedLanguages.includes('deformal') &&
        !allowedLanguages.includes('de')
      ) {
        // Special handling for 'deformal'
        finalLang = 'deformal';
      } else {
        // Otherwise, fall back to the brand's default language
        finalLang = brandLang;
      }
    } else if (brandSettings) {
      // If there's existing language in Redux, double check its allowed
      if (allowedLanguages?.includes(savedLang)) {
        return;
      } else {
        finalLang = brandLang;
      }
    }

    if (finalLang) {
      dispatch(setLanguage(finalLang));
      setTimeout(() => dispatch(getTexts()), 500);
    }

    // Handle cached languages for old users who have cached language as 'de' but brand supports only 'deformal'
    if (
      savedLang &&
      savedLang === 'de' &&
      !allowedLanguages.includes('de') &&
      allowedLanguages.includes('deformal')
    ) {
      dispatch(setLanguage('deformal'));
    }

    // Update user locale if there's a mismatch or no saved value
    if (
      (user && !user.locale) ||
      (user?.locale && user?.locale !== finalLang)
    ) {
      dispatch(updateUser({ locale: finalLang }));
    }
  }, [user?.version, brandSettings?.allowedLanguages]);
};
